/**
 * TodayEventList — full-card event list for /i-dag (Plan 03-09 D-12).
 *
 * Renders today's events as EventCard variant="full".
 * All-day events float to top per D-11 pattern.
 */
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../components/primitives';
import { EventCard } from '../calendar/EventCard';
import type { EventInstance } from '../calendar/useCalendarRange';

export function TodayEventList({
  events,
  onEventClick,
}: {
  events: EventInstance[];
  onEventClick?: (e: EventInstance) => void;
}) {
  const { t } = useTranslation();

  // Plan 06-02 Task 2: replace the inline `<p>` empty fallback with
  // <EmptyState variant="inline"> driven by calendar.empty.today.{heading,body}.
  // The "today" surface reuses calendar.* keys per UI-SPEC §Copywriting.
  // Outer wrapper preserves the today-events-empty data-testid.
  if (events.length === 0) {
    return (
      <div data-testid="today-events-empty">
        <EmptyState
          variant="inline"
          heading={t('calendar.empty.today.heading')}
          body={t('calendar.empty.today.body')}
        />
      </div>
    );
  }

  // All-day events float to top; timed events sorted by starts_at.
  // Sorter på øyeblikk, ikke streng: serier har Oslo-offset, enkeltavtaler «Z» (#137).
  const sorted = [...events].sort((a, b) => {
    if (a.all_day && !b.all_day) return -1;
    if (!a.all_day && b.all_day) return 1;
    return Date.parse(a.starts_at) - Date.parse(b.starts_at);
  });

  return (
    <ul className="space-y-3" data-testid="today-events-list">
      {sorted.map((ev) => (
        <li key={ev.id}>
          <EventCard
            variant="full"
            id={ev.id}
            title={ev.title}
            starts_at={ev.starts_at}
            ends_at={ev.ends_at}
            all_day={ev.all_day}
            location={ev.location}
            color_token={ev.color_token}
            attendee_ids={ev.attendee_ids}
            onClick={() => onEventClick?.(ev)}
          />
        </li>
      ))}
    </ul>
  );
}
